import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartPie,
  faInfoCircle,
  faExclamationTriangle,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";

const EtaComplianceChart = ({ data, calculateEtaCompliance, onItemClick }) => {
  const [complianceData, setComplianceData] = useState({
    compliant: 0,
    nonCompliant: 0,
    noData: 0,
    breakdownByDays: [],
  });

  useEffect(() => {
    if (!data || data.length === 0) {
      setComplianceData({
        compliant: 0,
        nonCompliant: 0,
        noData: 0,
        breakdownByDays: [],
      });
      return;
    }

    // Count compliant and non-compliant references
    let compliantCount = 0;
    let nonCompliantCount = 0;
    let noDataCount = 0;

    // For tracking references by days difference
    const daysDifferenceMap = {};

    data.forEach((item) => {
      const result = calculateEtaCompliance(item);

      if (result.daysDifference === null) {
        noDataCount++;
      } else if (result.compliant) {
        compliantCount++;

        // Add to days breakdown
        const days = result.daysDifference;
        daysDifferenceMap[days] = (daysDifferenceMap[days] || 0) + 1;
      } else {
        nonCompliantCount++;

        // Add to days breakdown
        const days = result.daysDifference;
        daysDifferenceMap[days] = (daysDifferenceMap[days] || 0) + 1;
      }
    });

    // Convert days breakdown to sorted array
    const breakdownByDays = Object.entries(daysDifferenceMap)
      .map(([days, count]) => ({
        days: parseInt(days, 10),
        count,
        compliant: parseInt(days, 10) <= 7,
      }))
      .sort((a, b) => a.days - b.days);

    setComplianceData({
      compliant: compliantCount,
      nonCompliant: nonCompliantCount,
      noData: noDataCount,
      breakdownByDays,
    });
  }, [data, calculateEtaCompliance]);

  // Calculate percentages
  const total =
    complianceData.compliant +
    complianceData.nonCompliant +
    complianceData.noData;
  const compliantPercentage = total
    ? Math.round((complianceData.compliant / total) * 100)
    : 0;
  const nonCompliantPercentage = total
    ? Math.round((complianceData.nonCompliant / total) * 100)
    : 0;
  const noDataPercentage = total
    ? Math.round((complianceData.noData / total) * 100)
    : 0;

  if (total === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-500">
        <FontAwesomeIcon icon={faChartPie} className="text-4xl mb-2" />
        <p>No ETA compliance data available</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-800 flex items-center">
          <FontAwesomeIcon icon={faChartPie} className="mr-2 text-green-600" />
          ETA Compliance (7-Day KPI)
        </h3>
        <div className="text-xs text-gray-500 flex items-center">
          <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
          ETA to Alta difference
        </div>
      </div>

      <div className="flex-grow">
        {/* Donut Chart */}
        <div className="flex items-center justify-center mb-4 relative">
          <div className="w-32 h-32 rounded-full flex items-center justify-center relative bg-gray-100">
            {/* Compliant Segment */}
            <svg className="w-32 h-32 transform -rotate-90 absolute top-0 left-0">
              <circle
                cx="64"
                cy="64"
                r="56"
                strokeWidth="16"
                stroke="#10B981"
                fill="transparent"
                strokeDasharray="352"
                strokeDashoffset={352 * (1 - compliantPercentage / 100)}
                className="transition-all duration-1000 ease-in-out"
              />
            </svg>

            {/* Non-Compliant Segment */}
            <svg className="w-32 h-32 transform -rotate-90 absolute top-0 left-0">
              <circle
                cx="64"
                cy="64"
                r="56"
                strokeWidth="16"
                stroke="#EF4444"
                fill="transparent"
                strokeDasharray="352"
                strokeDashoffset={
                  352 *
                  (1 - (compliantPercentage + nonCompliantPercentage) / 100)
                }
                className="transition-all duration-1000 ease-in-out"
              />
            </svg>

            {/* No Data Segment */}
            <svg className="w-32 h-32 transform -rotate-90 absolute top-0 left-0">
              <circle
                cx="64"
                cy="64"
                r="56"
                strokeWidth="16"
                stroke="#9CA3AF"
                fill="transparent"
                strokeDasharray="352"
                strokeDashoffset={
                  352 *
                  (1 -
                    (compliantPercentage +
                      nonCompliantPercentage +
                      noDataPercentage) /
                      100)
                }
                className="transition-all duration-1000 ease-in-out"
              />
            </svg>

            {/* Center */}
            <div className="bg-white w-20 h-20 rounded-full flex items-center justify-center z-10">
              <div className="text-center">
                <div className="text-xl font-bold text-gray-800">
                  {compliantPercentage}%
                </div>
                <div className="text-xs text-gray-500">Compliant</div>
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="ml-6 space-y-2">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
              <div className="text-sm">
                <span className="text-gray-700">Compliant: </span>
                <span className="font-medium">{complianceData.compliant}</span>
              </div>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
              <div className="text-sm">
                <span className="text-gray-700">Non-compliant: </span>
                <span className="font-medium">
                  {complianceData.nonCompliant}
                </span>
              </div>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-gray-400 rounded-full mr-2"></div>
              <div className="text-sm">
                <span className="text-gray-700">No data: </span>
                <span className="font-medium">{complianceData.noData}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Days Breakdown - Horizontal Bar Chart */}
        <div className="mt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Days from Alta to ETA
          </h4>
          <div className="space-y-2 overflow-y-auto max-h-28">
            {complianceData.breakdownByDays.map((item) => (
              <div key={item.days} className="group">
                <div className="flex justify-between items-center mb-1">
                  <div className="text-xs flex items-center">
                    <span className="font-medium text-gray-700">
                      {item.days} days
                    </span>
                    {item.days > 7 && (
                      <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        className="ml-1 text-yellow-500 text-xs"
                      />
                    )}
                  </div>
                  <div className="text-xs text-gray-500">{item.count}</div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`${
                      item.compliant ? "bg-green-500" : "bg-red-500"
                    } h-full rounded-full`}
                    style={{
                      width: `${
                        (item.count /
                          Math.max(
                            ...complianceData.breakdownByDays.map(
                              (d) => d.count
                            )
                          )) *
                        100
                      }%`,
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-gray-200">
        <button
          onClick={onItemClick}
          className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 transition-colors duration-200 rounded-md"
        >
          <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
          View Non-Compliant References
        </button>
      </div>
    </div>
  );
};

export default EtaComplianceChart;
